import { useEventListener, watchImmediate } from "@vueuse/core";
import { computed, onMounted, ref, shallowRef, toValue } from "vue";

import { deepActiveElement } from "#ce/utils/focus.js";

// The mobile URL bar collapsing/expanding also resizes the visual viewport, by
// ~50px; software keyboards take 200px+. Insets below this are not a keyboard.
// The threshold cannot distinguish a keyboard from pinch-zoom, so a pinch-zoomed
// viewport with a focused field also lifts the sheet.
const KEYBOARD_MIN_INSET = 60;

// Input types that never raise a software keyboard. Focusing one must not count
// as text entry, or stale visual-viewport metrics would re-apply the inset.
const KEYBOARDLESS_INPUT_TYPES = [
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
];

/**
 * Whether the element takes typed text and therefore raises a software
 * keyboard. Deliberately narrower than "occludes the viewport bottom": a
 * focused `<select>` (or other picker surface) is not covered, so the sheet
 * stays put behind a picker wheel.
 */
function isTextEntry(element) {
  if (!element) {
    return false;
  }
  if (element.matches("textarea") || element.isContentEditable) {
    return true;
  }

  return element.matches("input") && !KEYBOARDLESS_INPUT_TYPES.includes(element.type);
}

/**
 * Track the visual viewport for the bottom sheet as two reactive CSS lengths:
 *
 * - `viewportUnit` — one percent of the visible viewport height in pixels.
 *   Prefers `visualViewport.height` (which excludes the mobile browser's
 *   dynamic URL bar, unlike `100vh`) and falls back to `innerHeight`. The sheet
 *   multiplies it by a percentage to size itself against the actually-visible
 *   viewport.
 * - `keyboardInset` — the height of the software keyboard in layout-viewport
 *   pixels, `"0px"` while no keyboard is up. A fixed bottom-anchored element
 *   sits on the layout viewport's bottom edge, which the keyboard covers
 *   without moving (the keyboard shrinks/pans only the visual viewport), so the
 *   sheet raises its bottom edge by this inset to stay visible. Guarded twice:
 *   the inset must exceed the URL-bar delta AND a text-entry element must hold
 *   focus — the focus guard also zeroes the inset on blur even when iOS (26)
 *   reports stale visual-viewport metrics after keyboard dismissal. While
 *   `openRef` is falsy the focus listeners are detached and the computed is
 *   pinned to `"0px"`, so focus changes on a page with closed sheets cost
 *   nothing; the open-transition remeasure seeds the active element.
 *
 * Viewport metrics remeasure on window/visualViewport resize, visualViewport
 * scroll (iOS pans the visual viewport to reveal the caret without resizing
 * it), orientation change, and once on mount so the first frame reflects the
 * real viewport rather than the setup-time guess. Focus is tracked reactively
 * via composed `focusin`/`focusout` (piercing shadow roots), decoupled from
 * the metrics.
 *
 * @param {import("vue").MaybeRefOrGetter<boolean>} [openRef] - When supplied,
 *   the metrics are remeasured every time it becomes truthy and the keyboard
 *   inset is active only while it is truthy. The sheet passes its `open` ref so
 *   the values reflect the CURRENT viewport on open rather than ones cached at
 *   mount — a viewport change that fired no `resize`/`orientationchange`
 *   (DevTools desktop→mobile switch, mobile keyboard, URL-bar collapse, bfcache
 *   restore) would otherwise leave them stale and the sheet would overflow
 *   offscreen.
 * @returns {{
 *   viewportUnit: import("vue").ComputedRef<string>,
 *   keyboardInset: import("vue").ComputedRef<string>,
 * }} `"<n>px"` lengths.
 */
export function useViewportHeight(openRef) {
  // Not VueUse's useActiveElement: it tracks window `focus`/`blur` capture
  // events, which iOS WebKit does not reliably deliver for focus moves inside
  // nested shadow roots — its ref then reports a stale element while the
  // keyboard is up. Composed `focusin`/`focusout` on the document cross shadow
  // boundaries on every engine.
  const activeElement = shallowRef(deepActiveElement());
  const metrics = ref(measure());

  function trackActiveElement() {
    activeElement.value = deepActiveElement();
  }

  // Detached while the sheet is closed; the open-transition remeasure seeds
  // the active element, so nothing is missed between attachments.
  const focusTarget = () => (!openRef || toValue(openRef) ? document : null);

  useEventListener(focusTarget, "focusin", trackActiveElement);
  useEventListener(focusTarget, "focusout", () => {
    trackActiveElement();
    // iOS WebKit can still report the outgoing element while `focusout`
    // dispatches (the same settling lag the chain shows around `focusin`), and
    // a keyboard dismissal with stale metrics fires no later viewport event —
    // one re-read after settling keeps a nonzero inset from outliving it.
    requestAnimationFrame(trackActiveElement);
  });

  function measure() {
    return {
      innerHeight: window.innerHeight,
      viewportHeight: window.visualViewport?.height,
      viewportOffsetTop: window.visualViewport?.offsetTop ?? 0,
    };
  }

  function remeasure() {
    metrics.value = measure();
    // iOS WebKit settles the shadow-piercing activeElement chain only after
    // `focusin` has already fired, so a focus-event snapshot can be stale.
    // Every viewport event (the keyboard's resize arrives ~250ms after focus)
    // re-reads it; the reverse coupling stays severed — focus events never
    // touch the metrics.
    trackActiveElement();
  }

  /** Remeasure when the sheet opens — its size is only known once it is visible. */
  function remeasureWhenOpen(open) {
    if (open) {
      remeasure();
    }
  }

  const viewportUnit = computed(() => {
    const height = metrics.value.viewportHeight ?? metrics.value.innerHeight;
    return `${height * 0.01}px`;
  });

  const keyboardInset = computed(() => {
    // Gate before reading the active element: a closed sheet's computed then
    // never tracks it, so focus changes don't re-evaluate this at all.
    if (openRef && !toValue(openRef)) {
      return "0px";
    }

    const { innerHeight, viewportHeight, viewportOffsetTop } = metrics.value;
    if (viewportHeight === undefined || !isTextEntry(activeElement.value)) {
      return "0px";
    }

    // Distance from the visual viewport's bottom edge to the layout viewport's,
    // i.e. how far the keyboard intrudes past the sheet's natural anchor.
    const inset = innerHeight - viewportHeight - viewportOffsetTop;
    if (inset < KEYBOARD_MIN_INSET) {
      return "0px";
    }

    return `${inset}px`;
  });

  useEventListener(window, "resize", remeasure);
  useEventListener(window, "orientationchange", remeasure);
  // The visual viewport can change without firing a window `resize` — the mobile
  // keyboard showing/hiding or the URL bar collapsing — so track it directly to
  // keep an open sheet sized to the actually-visible area. Guarded via a getter
  // so it no-ops where `visualViewport` is unavailable.
  useEventListener(() => window.visualViewport, "resize", remeasure);
  useEventListener(() => window.visualViewport, "scroll", remeasure);
  onMounted(remeasure);

  if (openRef) {
    watchImmediate(openRef, remeasureWhenOpen);
  }

  return { viewportUnit, keyboardInset };
}
